import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Focus ring (CH-09). The ring is drawn at FULL opacity and 2px, not the
 * former `ring-ring/30 ring-1`: a 30%-alpha ring composites to ~1.5:1
 * against every surface it can land on (page, chrome, sidebar, popover),
 * which is well under the 3:1 WCAG 2.x non-text contrast minimum for a
 * focus indicator. Opaque `--ring` measures 5.6:1 on light and 6.0:1 on
 * dark against those same surfaces. `ring-offset-1` keeps the ring off
 * the button's own fill, which matters most for the `default` variant
 * where fill and ring are the same blue.
 *
 * The destructive variant deliberately does NOT get a red ring. `--destructive`
 * measures 2.85:1 on light and 1.82:1 on dark against the surfaces a ring can
 * land on — it cannot pass 3:1 without retuning the token. It takes the same
 * blue `--ring` as everything else, which is also what the accent system says:
 * focus is structure, and structure is blue.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive-fill text-destructive-foreground shadow-xs hover:bg-destructive-fill/90",
        outline:
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      /**
       * Control-size scale (CH-22 / PP-31). The band the plan calls "normal"
       * is 32–36px and is spanned by `sm` (32) and `default` (36); `xs` (28)
       * is the compact step for dense surfaces — dialogs, filter bars,
       * toolbars. `input.tsx` and `select.tsx` use the same three names for
       * the same three heights so a compact row composes without arithmetic.
       *
       * `default` is unchanged and stays the default: these primitives have
       * hundreds of call sites, and moving the default would silently resize
       * every form, toolbar and filter bar in the app — including the
       * surfaces Wave 4 (grids) and Wave 5 (per-surface polish) own. Callers
       * opt in to `xs`.
       */
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        xs: "h-7 rounded-md gap-1 px-2.5 has-[>svg]:px-2",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
